/*
 * Select: pick one of a short list (under ~12 options; use Combobox for longer or searchable lists).
 *   <Select value={country} onValueChange={setCountry} options={[{ value: 'SA', label: t('countries.SA') }]} />
 * With react-hook-form, use <Controller> (see features/auth/pages/SignUpPage.tsx).
 */
import { CaretDown, Check } from '@phosphor-icons/react'
import { Select as S } from 'radix-ui'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'
import { controlClasses, controlHeight } from './control'
import { useFieldControl } from './Field'

export type SelectOption = {
  value: string
  label: ReactNode
  icon?: ReactNode
  description?: ReactNode
  disabled?: boolean
}

export function Select({
  value,
  onValueChange,
  options,
  placeholder,
  disabled,
  name,
  className,
  start,
  'aria-label': ariaLabel,
}: {
  value?: string
  onValueChange: (v: string) => void
  options: SelectOption[]
  placeholder?: string
  disabled?: boolean
  name?: string
  className?: string
  start?: ReactNode
  'aria-label'?: string
}) {
  const { t } = useTranslation('common')
  const field = useFieldControl()
  const selected = options.find((o) => o.value === value)
  return (
    <S.Root value={value || undefined} onValueChange={onValueChange} disabled={disabled} name={name}>
      <S.Trigger
        id={field?.id}
        aria-describedby={field?.describedBy}
        aria-invalid={field?.invalid || undefined}
        aria-label={ariaLabel}
        className={cn(
          controlClasses,
          controlHeight,
          'inline-flex items-center gap-2 px-3 text-start data-[placeholder]:text-subtle',
          className,
        )}
      >
        {(start ?? selected?.icon) ? (
          <span className="text-subtle [&_svg]:size-[18px]">{start ?? selected?.icon}</span>
        ) : null}
        <span className="min-w-0 flex-1 truncate">
          <S.Value placeholder={placeholder ?? t('ui.select.placeholder')}>{selected?.label}</S.Value>
        </span>
        <S.Icon asChild>
          <CaretDown className="size-4 shrink-0 text-subtle" aria-hidden="true" />
        </S.Icon>
      </S.Trigger>
      <S.Portal>
        <S.Content
          position="popper"
          sideOffset={6}
          collisionPadding={8}
          className="z-50 max-h-[min(22rem,var(--radix-select-content-available-height))] min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-2xl bg-surface p-1.5 shadow-pop ring-1 ring-hairline data-[state=open]:animate-pop-in"
        >
          <S.Viewport className="scrollbar-thin">
            {options.map((o) => (
              <S.Item
                key={o.value}
                value={o.value}
                disabled={o.disabled}
                className="relative flex min-h-10 cursor-pointer items-center gap-2.5 rounded-xl py-2 ps-3 pe-9 text-14 text-ink outline-none select-none data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 data-[highlighted]:bg-surface-tint"
              >
                {o.icon ? <span className="text-subtle [&_svg]:size-[18px]">{o.icon}</span> : null}
                <span className="flex min-w-0 flex-col">
                  <S.ItemText>{o.label}</S.ItemText>
                  {o.description ? <span className="text-12 text-subtle">{o.description}</span> : null}
                </span>
                <S.ItemIndicator className="absolute end-3 text-brand-600">
                  <Check className="size-4" weight="bold" aria-hidden="true" />
                </S.ItemIndicator>
              </S.Item>
            ))}
          </S.Viewport>
        </S.Content>
      </S.Portal>
    </S.Root>
  )
}
